import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import * as XLSX from 'xlsx';

export interface ProductCM1Data {
  product_id: string;
  product_name: string;
  revenue: number;
  cogs: number;
  damage_loss: number;
  cm1: number;
  cm1_margin: number; // CM1 as percentage of revenue
  quantity_sold: number;
  quantity_damaged: number;
}

export interface CM1Summary {
  totalRevenue: number;
  totalCogs: number;
  totalDamageLoss: number;
  totalCm1: number;
  overallMargin: number;
  productCount: number;
}

export interface CM1ReportData {
  products: ProductCM1Data[];
  summary: CM1Summary;
  periodStart: string;
  periodEnd: string;
}

interface SalesData {
  product_name: string;
  quantity: number;
  price: number;
}

interface ConsumptionData {
  product_name: string;
  consumption_type: string;
  quantity_consumed: number;
  total_cost: number;
}

// Fetch CM1 report data for a date range
export async function getCM1ReportData(
  startDate: Date,
  endDate: Date
): Promise<CM1ReportData> {
  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  // Fetch all data sources in parallel
  const [chipchipSales, b2bDeliveries, consumptionData] = await Promise.all([
    // ChipChip sales data
    supabase
      .from('chipchip_sales_data')
      .select('product_name, quantity, price')
      .gte('date', startStr)
      .lte('date', endStr),
    
    // B2B delivery data
    supabase
      .from('b2b_delivery_reports')
      .select('product_name, net_quantity_delivered')
      .gte('delivery_date', startStr)
      .lte('delivery_date', endStr),
    
    // Batch consumption data with costs
    supabase
      .from('batch_consumption_log')
      .select(`
        quantity_consumed,
        unit_cost_at_consumption,
        consumption_type,
        batch_layer:inventory_batch_layers(
          product:products(id, name)
        )
      `)
      .gte('reference_date', startStr)
      .lte('reference_date', endStr)
  ]);

  if (chipchipSales.error) throw chipchipSales.error;
  if (b2bDeliveries.error) throw b2bDeliveries.error;
  if (consumptionData.error) throw consumptionData.error;

  // Also get B2B product prices for revenue calculation
  const { data: b2bProducts, error: b2bProductsError } = await supabase
    .from('b2b_products')
    .select('product_name, price');
  
  if (b2bProductsError) throw b2bProductsError;

  // Build B2B price lookup
  const b2bPriceMap = new Map<string, number>();
  (b2bProducts || []).forEach((p: { product_name: string; price: number }) => {
    b2bPriceMap.set(p.product_name.toLowerCase(), p.price);
  });

  // Aggregate data by product
  const productMap = new Map<string, {
    product_id: string;
    product_name: string;
    revenue: number;
    cogs: number;
    damage_loss: number;
    quantity_sold: number;
    quantity_damaged: number;
  }>();

  // Helper to get or create product entry
  const getProduct = (name: string, id?: string) => {
    const key = name.toLowerCase();
    if (!productMap.has(key)) {
      productMap.set(key, {
        product_id: id || key,
        product_name: name,
        revenue: 0,
        cogs: 0,
        damage_loss: 0,
        quantity_sold: 0,
        quantity_damaged: 0
      });
    }
    return productMap.get(key)!;
  };

  // Process ChipChip sales (revenue)
  (chipchipSales.data || []).forEach((sale: SalesData) => {
    const product = getProduct(sale.product_name);
    product.revenue += sale.quantity * sale.price;
    product.quantity_sold += sale.quantity;
  });

  // Process B2B deliveries (revenue)
  (b2bDeliveries.data || []).forEach((delivery: { product_name: string; net_quantity_delivered: number | null }) => {
    const product = getProduct(delivery.product_name);
    const qty = delivery.net_quantity_delivered || 0;
    const price = b2bPriceMap.get(delivery.product_name.toLowerCase()) || 0;
    product.revenue += qty * price;
    product.quantity_sold += qty;
  });

  // Process batch consumption (COGS and Damage)
  (consumptionData.data || []).forEach((consumption: any) => {
    const productInfo = consumption.batch_layer?.product;
    if (!productInfo) return;

    const product = getProduct(productInfo.name, productInfo.id);
    const totalCost = consumption.quantity_consumed * consumption.unit_cost_at_consumption;

    if (consumption.consumption_type === 'damage') {
      product.damage_loss += totalCost;
      product.quantity_damaged += consumption.quantity_consumed;
    } else {
      // sale, chipchip_sale, b2b_delivery, etc.
      product.cogs += totalCost;
    }
  });

  // Convert to array and calculate CM1
  const products: ProductCM1Data[] = Array.from(productMap.values())
    .map(p => ({
      ...p,
      cm1: p.revenue - p.cogs - p.damage_loss,
      cm1_margin: p.revenue > 0 ? ((p.revenue - p.cogs - p.damage_loss) / p.revenue) * 100 : 0
    }))
    .filter(p => p.revenue > 0 || p.cogs > 0 || p.damage_loss > 0)
    .sort((a, b) => b.cm1 - a.cm1);

  // Calculate summary
  const summary: CM1Summary = {
    totalRevenue: products.reduce((sum, p) => sum + p.revenue, 0),
    totalCogs: products.reduce((sum, p) => sum + p.cogs, 0),
    totalDamageLoss: products.reduce((sum, p) => sum + p.damage_loss, 0),
    totalCm1: products.reduce((sum, p) => sum + p.cm1, 0),
    overallMargin: 0,
    productCount: products.length
  };
  summary.overallMargin = summary.totalRevenue > 0 
    ? (summary.totalCm1 / summary.totalRevenue) * 100 
    : 0;

  return {
    products,
    summary,
    periodStart: startStr,
    periodEnd: endStr
  };
}

// Get CM1 trend data for charts
export async function getCM1TrendData(
  startDate: Date,
  endDate: Date,
  granularity: 'daily' | 'weekly' | 'monthly' = 'daily'
): Promise<Array<{
  date: string;
  revenue: number;
  cogs: number;
  damage_loss: number;
  cm1: number;
}>> {
  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');

  // Fetch sales and consumption data
  const [chipchipSales, consumptionData] = await Promise.all([
    supabase
      .from('chipchip_sales_data')
      .select('date, quantity, price')
      .gte('date', startStr)
      .lte('date', endStr)
      .order('date'),
    
    supabase
      .from('batch_consumption_log')
      .select('reference_date, quantity_consumed, unit_cost_at_consumption, consumption_type')
      .gte('reference_date', startStr)
      .lte('reference_date', endStr)
      .order('reference_date')
  ]);

  if (chipchipSales.error) throw chipchipSales.error;
  if (consumptionData.error) throw consumptionData.error;

  // Aggregate by date
  const dateMap = new Map<string, { revenue: number; cogs: number; damage_loss: number }>();

  const getDateEntry = (date: string) => {
    if (!dateMap.has(date)) {
      dateMap.set(date, { revenue: 0, cogs: 0, damage_loss: 0 });
    }
    return dateMap.get(date)!;
  };

  // Process sales
  (chipchipSales.data || []).forEach((sale: { date: string; quantity: number; price: number }) => {
    const entry = getDateEntry(sale.date);
    entry.revenue += sale.quantity * sale.price;
  });

  // Process consumption
  (consumptionData.data || []).forEach((c: any) => {
    const entry = getDateEntry(c.reference_date);
    const cost = c.quantity_consumed * c.unit_cost_at_consumption;
    if (c.consumption_type === 'damage') {
      entry.damage_loss += cost;
    } else {
      entry.cogs += cost;
    }
  });

  // Convert to sorted array
  return Array.from(dateMap.entries())
    .map(([date, data]) => ({
      date,
      ...data,
      cm1: data.revenue - data.cogs - data.damage_loss
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Export CM1 report to Excel
export function exportCM1ReportToExcel(
  report: CM1ReportData,
  filename: string
) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ['CM1 Profitability Report'],
    [''],
    ['Report Period', `${report.periodStart} to ${report.periodEnd}`],
    [''],
    ['Summary Metrics'],
    ['Total Revenue', report.summary.totalRevenue.toFixed(2)],
    ['Total COGS', report.summary.totalCogs.toFixed(2)],
    ['Total Damage Loss', report.summary.totalDamageLoss.toFixed(2)],
    ['Total CM1', report.summary.totalCm1.toFixed(2)],
    ['Overall Margin %', report.summary.overallMargin.toFixed(1) + '%'],
    ['Products with Activity', report.summary.productCount],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Product Detail sheet
  const productData = report.products.map(p => ({
    'Product': p.product_name,
    'Revenue (ETB)': p.revenue.toFixed(2),
    'COGS (ETB)': p.cogs.toFixed(2),
    'Damage Loss (ETB)': p.damage_loss.toFixed(2),
    'CM1 (ETB)': p.cm1.toFixed(2),
    'CM1 Margin %': p.cm1_margin.toFixed(1) + '%',
    'Qty Sold': p.quantity_sold.toFixed(2),
    'Qty Damaged (kg)': p.quantity_damaged.toFixed(2),
  }));
  const productSheet = XLSX.utils.json_to_sheet(productData);
  XLSX.utils.book_append_sheet(workbook, productSheet, 'Product Detail');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}
